<template>
  <div class="carousel w-100">
    <div class="carousel-image">
      <div v-if="images.length === 0" class="no-images">
        <i class="fa-solid fa-image"></i>
        <p>No hay imágenes en la galería</p>
      </div>

      <div v-else class="slider-wrapper">
        <div class="slider" :style="{ transform: `translateX(-${current * 100}%)` }">
          <div
            v-for="(item, index) in localImages"
            :key="index"
            class="slide"
          >
            <template v-if="item.type === 'video'">
              <video
                :src="item.img"
                controls
                width="800"
                height="800"
                ref="videos"
                @play="handleVideoPlay"
                @ended="handleVideoEnded"
              ></video>
            </template>
            <template v-else>
              <img
                :src="item.img"
                alt="Galería"
                :loading="index === 0 ? 'eager' : 'lazy'"
                width="800"
                height="800"
              />
            </template>
          </div>
        </div>
      </div>


      <div v-if="showControls" class="overlay-controls">
        <button @click="prev(true)">&#8592;</button>

        <slot>
          <div class="thumbnails custom-scroll">
            <template v-for="(item, index) in images" :key="index">
              <video
                v-if="item.type === 'video'"
                :src="item.img"
                :class="{ active: index === current }"
                @click="goTo(index)"
                muted
                width="75"
                height="50"
              ></video>
              <img
                v-else
                :src="item.img"
                :class="{ active: index === current }"
                @click="goTo(index)"
                alt="Miniatura"
                loading="lazy"
                width="75"
                height="50"
              />
            </template>
          </div>
        </slot>

        <button @click="next(true)">&#8594;</button>
      </div>
    </div>
  </div>
</template>
  
<script>
  export default {
    name: 'ImageCarousel',
    props: {
      images: {
        type: Array,
        default: () => [
          { img: "https://picsum.photos/id/1015/800/400", sort: 1 },
          { img: "https://picsum.photos/id/1016/800/400", sort: 2 },
          { img: "https://picsum.photos/id/1020/800/400", sort: 4},
          { img: "https://picsum.photos/id/1018/800/400", sort: 3 },
        ]
      },
      manageControls: {
        type: Boolean,
        default: false
      },
      autoSlideInterval: {
        type: Number,
        default: 4000
      },
      autoSlide: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        current: 0,
        intervalId: null,
        localImages: []
      };
    },
    methods: {
      next(reset = false) {
        this.pauseCurrentVideo();
        this.current = (this.current + 1) % this.images.length;
        if (reset) this.restartAutoSlide();
      },
      prev(reset = false) {
        this.pauseCurrentVideo();
        this.current = this.current === 0 ? this.images.length - 1 : this.current - 1;
        if (reset) this.restartAutoSlide();
      },
      goTo(index) {
        this.pauseCurrentVideo();
        this.current = index;
        this.restartAutoSlide();
      },
      startAutoSlide() {
        if (this.autoSlide && this.images.length > 0) {
          this.intervalId = setInterval(() => {
            this.next();
          }, this.autoSlideInterval);
        }
      },
      stopAutoSlide() {
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      },
      restartAutoSlide() {
        this.stopAutoSlide();
        this.startAutoSlide();
      },
      pauseCurrentVideo() {
        const videos = this.$refs.videos;
        if (Array.isArray(videos)) {
          videos.forEach(video => {
            if (!video.paused) video.pause();
          });
        } else if (videos && !videos.paused) {
          videos.pause();
        }
      },
      handleVideoPlay() {
        this.stopAutoSlide();
      },
      handleVideoEnded() {
        this.next();
        this.restartAutoSlide();
      }
    },
    watch: {
      images: {
        handler(newImages) {
          this.localImages = [...newImages]
            .map(img => ({ type: img.type ? img.type : 'image', ...img }))
            .sort((a, b) => a.sort - b.sort);
          if (this.current!== 0) this.current = 0
        },
        immediate: true,
        deep: true
      }
    },
    computed: {
      showControls() {
        if (this.manageControls) return true;
        return this.images.length > 1;
      },
    },
    mounted() {
      this.startAutoSlide();
    },
    beforeUnmount() {
      this.stopAutoSlide();
    }
  };
</script>

<style scoped>

.carousel-image {
  aspect-ratio: 1 / 1;
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
}

.no-images {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ccc;
  text-align: center;
  font-size: 18px;
}

.no-images i {
  font-size: 48px;
  margin-bottom: 8px;
  color: #bbb;
}
  
.thumbnails {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding: 0 10px;
    overflow-y: hidden;
}
 
  
.thumbnails img {
  margin-top:10px;
  margin-bottom:6px;
  object-fit: cover;
  border-radius: 4px;
  opacity: 0.6;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.thumbnails video {
  margin-top:10px;
  margin-bottom:6px;
  object-fit: cover;
  border-radius: 4px;
  opacity: 0.6;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}
  
.thumbnails img:hover {
    opacity: 1;
    transform: scale(1.15);
    transition: all 0.3s ease;
}

.thumbnails video:hover {
    opacity: 1;
    transform: scale(1.15);
    transition: all 0.3s ease;
}
  
.thumbnails img.active {
    border-color: white;
    opacity: 1;
}

.thumbnails video.active {
    border-color: white;
    opacity: 1;
}
  
.overlay-controls {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0, 0, 0, 0.4);
    padding: 0px 16px;
    border-radius: 0 0 12px 12px;
}
  
.overlay-controls button {
    background: rgba(255, 255, 255, 0.8);
    border: none;
    padding: 6px 12px;
    font-size: 18px;
    cursor: pointer;
    border-radius: 6px;
}
  
.overlay-controls button:hover {
    background: white;
}
  
.carousel {
    margin: 0 auto;
    text-align: center;
    position: relative;
}
.slider-wrapper {
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  position: relative;
  border-radius: 12px;
}

.slider {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

.slide {
  width: 100%;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* respeta aspecto sin recortar */
  border-radius: 12px;
}

.slide video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
}
</style>
    